/**
 * Supabase Image Transformation Utility
 * Documentation: https://supabase.com/docs/guides/storage/serving/image-transformations
 *
 * Supports on-the-fly image transformations for Supabase Storage URLs:
 * - Resize (width, height)
 * - Format conversion (webp, avif, origin)
 * - Quality adjustment
 * - Resize modes (cover, contain, fill)
 */

/**
 * Check if browser supports modern image formats
 * Cached results for performance
 */
let webpSupport = null;
let avifSupport = null;

const browserSupportsWebP = () => {
  if (webpSupport !== null) return webpSupport;

  // Modern browsers all support WebP, but we can still check
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;

  if (canvas.getContext && canvas.getContext("2d")) {
    webpSupport =
      canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  } else {
    webpSupport = false;
  }

  return webpSupport;
};

const browserSupportsAVIF = () => {
  if (avifSupport !== null) return avifSupport;

  // Check for AVIF support using Image element (more reliable)
  // AVIF is supported in Chrome 85+, Firefox 93+, Safari 16+
  // For now, we can use User Agent as fallback
  const ua = navigator.userAgent.toLowerCase();

  // Chrome 85+, Edge 85+
  const chromeMatch = ua.match(/chrome\/(\d+)/);
  if (chromeMatch && parseInt(chromeMatch[1]) >= 85) {
    avifSupport = true;
    return avifSupport;
  }

  // Firefox 93+
  const firefoxMatch = ua.match(/firefox\/(\d+)/);
  if (firefoxMatch && parseInt(firefoxMatch[1]) >= 93) {
    avifSupport = true;
    return avifSupport;
  }

  // Safari 16+ (iOS 16+, macOS Ventura+)
  const safariMatch = ua.match(/version\/(\d+).*safari/);
  if (safariMatch && parseInt(safariMatch[1]) >= 16) {
    avifSupport = true;
    return avifSupport;
  }

  // Fallback to canvas test
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;

  if (canvas.getContext && canvas.getContext("2d")) {
    avifSupport =
      canvas.toDataURL("image/avif").indexOf("data:image/avif") === 0;
  } else {
    avifSupport = false;
  }

  return avifSupport;
};

/**
 * Transform options type definition
 * @typedef {Object} TransformOptions
 * @property {number} [width] - Target width in pixels
 * @property {number} [height] - Target height in pixels
 * @property {number} [quality=80] - Quality (1-100)
 * @property {('webp'|'avif'|'origin'|'auto')} [format='auto'] - Output format
 * @property {('cover'|'contain'|'fill')} [resize='cover'] - Resize mode
 */

/**
 * Generate optimized image URL with Supabase transformations
 * @param {string} url - Original Supabase storage URL
 * @param {TransformOptions} options - Transformation options
 * @returns {string} - Transformed image URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  // Return original URL if not a Supabase storage URL
  if (!url || !url.includes("/storage/v1/object/")) {
    return url;
  }

  const {
    width,
    height,
    quality = 80,
    format = "auto",
    resize = "cover",
  } = options;

  // Determine best format based on browser support
  let outputFormat = format;
  if (format === "auto") {
    if (browserSupportsAVIF()) {
      outputFormat = "avif";
    } else if (browserSupportsWebP()) {
      outputFormat = "webp";
    } else {
      outputFormat = "origin";
    }
  }

  // Build transformation parameters
  const params = new URLSearchParams();

  if (width) params.set("width", width.toString());
  if (height) params.set("height", height.toString());
  if (quality) params.set("quality", quality.toString());
  if (resize) params.set("resize", resize);
  if (outputFormat !== "origin") params.set("format", outputFormat);

  // Convert public URL to render endpoint for guaranteed transformations
  // From: https://project.supabase.co/storage/v1/object/public/bucket/path
  // To:   https://project.supabase.co/storage/v1/render/image/public/bucket/path
  let transformedUrl = url;

  if (url.includes("/storage/v1/object/public/")) {
    // Replace /object/public/ with /render/image/public/ for transformations
    transformedUrl = url.replace(
      "/storage/v1/object/public/",
      "/storage/v1/render/image/public/"
    );
  } else if (url.includes("/storage/v1/object/authenticated/")) {
    // For authenticated URLs
    transformedUrl = url.replace(
      "/storage/v1/object/authenticated/",
      "/storage/v1/render/image/authenticated/"
    );
  }

  // Add transformation parameters
  const separator = transformedUrl.includes("?") ? "&" : "?";
  return `${transformedUrl}${separator}${params.toString()}`;
};

/**
 * Preset configurations for common use cases
 */
export const ImagePresets = {
  // Gallery thumbnails - small, optimized
  thumbnail: {
    width: 300,
    height: 300,
    quality: 60,
    format: "auto",
    resize: "cover",
  },

  // Medium preview - balanced quality/size
  preview: {
    width: 480,
    height: 480,
    quality: 75,
    format: "auto",
    resize: "contain",
  },

  // Full size but optimized
  fullsize: {
    width: 1920,
    quality: 85,
    format: "auto",
    resize: "contain",
  },

  // Avatar/profile pictures
  avatar: {
    width: 200,
    height: 200,
    quality: 75,
    format: "auto",
    resize: "cover",
  },

  // High quality for download/sharing
  highQuality: {
    quality: 95,
    format: "auto",
    resize: "contain",
  },
};

/**
 * Generate multiple image URLs for responsive srcset
 * @param {string} url - Original Supabase storage URL
 * @param {TransformOptions} baseOptions - Base transformation options
 * @returns {Object} - Object with srcset string and sizes
 */
export const getResponsiveImageUrls = (url, baseOptions = {}) => {
  const widths = [320, 640, 960, 1280, 1920];

  const srcset = widths
    .map((width) => {
      const transformedUrl = getOptimizedImageUrl(url, {
        ...baseOptions,
        width,
      });
      return `${transformedUrl} ${width}w`;
    })
    .join(", ");

  return {
    srcset,
    sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
    src: getOptimizedImageUrl(url, { ...baseOptions, width: 960 }),
  };
};

/**
 * React hook for progressive image loading
 * @param {string} url - Image URL
 * @param {TransformOptions} options - Transformation options
 * @returns {Object} - Low quality placeholder and full quality URLs
 */
export const useProgressiveImage = (url, options = {}) => {
  const lqip = getOptimizedImageUrl(url, {
    ...options,
    width: 20,
    quality: 20,
    format: "webp",
  });

  const fullQuality = getOptimizedImageUrl(url, options);

  return { lqip, fullQuality };
};
